import { isEmpty } from 'lodash';
import { FC, ReactNode, useMemo } from 'react';
import useSWR, { Middleware } from 'swr';

import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { IconCaretRight } from 'src/general/Icons/IconCaretRight';
import { Button } from 'src/general/components/Button/Button';
import { CarouselSection } from 'src/general/components/Carousel/CarouselSection';
import { ConditionalWrapper } from 'src/general/components/ConditionalWrapper/ConditionalWrapper';
import { ProductCard } from 'src/plp/components/ProductCard/ProductCard';
import { ProductCardLoading } from 'src/plp/components/ProductCard/ProductCardLoading';
import { getPLPCarData } from 'src/services/CarService';
import { PlpFilterParams } from 'src/types/CarFilters.types';
import { ProductListDataPayload } from 'src/types/CataloguePage.types';
import { parseFilterParams } from 'src/utils/filterHelper';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import classes from './CarouselTabCars.module.scss';

const loadingTiles = [...Array(4).keys()].map((item) => ({
  id: `${item}`,
  tile: <ProductCardLoading key={`loading-${item}`} />,
}));

interface CarouselTabCarsProps {
  /** Name used to track GTM events */
  carouselName?: string;
  /** Name used to track GTM events */
  carouselValue?: string;
  queryParams: PlpFilterParams | null;
  footerBanner?: string;
  cta?: {
    label: ReactNode;
    url: string;
  };
  /** Handle the received product list data. */
  handleResponseData?: (data: ProductListDataPayload) => ProductListDataPayload;
}

export const CarouselTabCars: FC<CarouselTabCarsProps> = ({
  queryParams,
  footerBanner,
  cta,
  handleResponseData,
  carouselName,
  carouselValue,
}) => {
  const swrConfig = useMemo(
    () => (handleResponseData ? handleSWRResponseData(handleResponseData) : undefined),
    [handleResponseData],
  );

  const { data: productList } = useSWR<ProductListDataPayload>(
    queryParams === null
      ? null
      : {
          pageSize: 12,
          filters: parseFilterParams({ ...queryParams, state: 'available' }),
          sort: queryParams.sortBy,
        },
    getPLPCarData,
    swrConfig,
  );

  const tiles = useMemo(
    () =>
      productList && !isEmpty(productList.results)
        ? productList.results.map((product, index) => ({
            id: product.sku,
            tile: (
              <ProductCard
                carData={productDetailPayloadToBaseCarData(product)}
                weeklyRepayment={product.weeklyRepayment}
                defaultRate={product.defaultRate}
                variant="plp"
                onCardClick={
                  carouselName && carouselValue
                    ? () => {
                        const event = {
                          event: 'carousel_tile_click',
                          name: carouselName,
                          value: carouselValue,
                          index,
                          ecommerce: {
                            items: [
                              {
                                index: 0,
                                item_id: product.sku,
                                item_name: product.title,
                                currency: 'AUD',
                                item_brand: product.make,
                                item_category: product.model,
                                item_category2: product.body,
                                item_category3: product.transmission,
                                item_category4: product.ancapRating,
                                item_category5: product.buildYear,
                                item_variant: product.colour,
                                price: product.price,
                              },
                            ],
                          },
                        };
                        pushToDataLayer(event);
                      }
                    : undefined
                }
              />
            ),
          }))
        : loadingTiles,
    [productList?.results],
  );

  const hasFooterBanner = !isEmpty(footerBanner);

  return (
    <>
      <CarouselSection tiles={tiles} key={`${carouselValue}-${tiles.length}`} />
      {cta && (
        <footer className={classes.footer}>
          <ConditionalWrapper
            condition={hasFooterBanner}
            wrapper={(children) => (
              <div className={classes.footerBanner}>
                <div className={classes.footerBannerContent}>{footerBanner}</div>
                {children}
              </div>
            )}
          >
            <Button
              href={cta.url}
              variant={hasFooterBanner ? 'secondary' : 'primary'}
              size="medium"
              endIcon={<IconCaretRight />}
              onClick={() => {
                if (!isEmpty(carouselName) && !isEmpty(carouselValue)) {
                  pushToDataLayer({ event: 'carousel_footer_cta_click', name: carouselName, value: carouselValue });
                }
              }}
            >
              {cta.label}
            </Button>
          </ConditionalWrapper>
        </footer>
      )}
    </>
  );
};

/**
 * Returns a useSWR 'options' object, which adds the given function as middleware for processing response data.
 *
 * @see {@link https://swr.vercel.app/docs/middleware SWR Docs}
 */
function handleSWRResponseData<T extends Function>(handleResponseData: T): { use: Middleware[] } {
  return {
    use: [
      (useSWRNext) =>
        (...args) => {
          const response = useSWRNext(...args);
          return response.data ? { ...response, data: handleResponseData(response.data) } : response;
        },
    ],
  };
}
